// Transitional English translations for the final audited legacy UI surface.
//
// Keep this pack limited to application-owned interface copy. Dynamic rules
// translate only known UI shells and preserve dates, identifiers, counts, and
// other backend- or user-provided values verbatim.

const AVAILABILITY_LABELS = Object.freeze({
  Disponibil: 'Available',
  Indisponibil: 'Unavailable',
  'Șofer inactiv': 'Inactive driver',
  'Userul nu mai are rol de șofer': 'The user no longer has the driver role',
  'Șofer în concediu': 'Driver on leave',
  'Vehicul arhivat': 'Archived vehicle',
  'Vehicul în service': 'Vehicle under maintenance',
  'Există transporturi nefinalizate': 'There are unfinished transports',
  'Vehiculul are deja o intervenție activă': 'The vehicle already has an active maintenance job',
  'Transportul are mai mulți șoferi activi': 'The transport has multiple active drivers',
});

const translateAvailabilitySegment = (segment) => {
  const exact = AVAILABILITY_LABELS[segment];
  if (exact) return exact;

  const driverLeave = segment.match(/^Șofer în concediu:(.+)$/);
  if (driverLeave) return `Driver on leave:${driverLeave[1]}`;

  const unfinishedTransports = segment.match(/^Există transporturi nefinalizate(?: \((.+)\))$/);
  if (unfinishedTransports) {
    const details = unfinishedTransports[1]
      .replace(/^transporturi:/, 'transports:')
      .replace(/; Expeditor:/g, '; Freight forwarder:');
    return `There are unfinished transports (${details})`;
  }

  const multipleDrivers = segment.match(/^Transportul are mai mulți șoferi activi:(.+)$/);
  if (multipleDrivers) return `The transport has multiple active drivers:${multipleDrivers[1]}`;

  const unavailableReason = segment.match(/^Resursă indisponibilă \((.+)\)$/);
  if (unavailableReason) return `Resource unavailable (${unavailableReason[1]})`;

  // A backend-supplied display message is business data. Leave unknown text as
  // received instead of attempting an unsafe free-form translation.
  return segment;
};

const translateAvailabilityLabel = (label) => String(label)
  .split(' · ')
  .map(translateAvailabilitySegment)
  .join(' · ');

export const legacyEnglishRemaining = Object.freeze({
  // Authentication and account management
  'vezi parola': 'show password',
  'Vezi parola': 'Show password',
  'Trimite link de resetare': 'Send reset link',
  'Link-ul a expirat?': 'Has the link expired?',
  'Nu ai cont?': "Don't have an account?",
  Cont: 'Account',
  'Introdu parola': 'Enter your password',
  'Eroare de autentificare': 'Authentication error',

  // Authentication chrome retained by the legacy login components.
  'CONECTEAZA-TE': 'SIGN IN',
  LOGARE: 'SIGN IN',

  // Administration and sessions
  'Categorie activitate': 'Activity category',
  '⚙️ Administrare Sistem': '⚙️ System Administration',
  'Gestionare utilizatori': 'User Management',
  'Utilizator nou': 'New user',
  'Gestionare Sesiuni': 'Session Management',
  'sesiuni active': 'active sessions',
  'sesiuni active simultan': 'concurrent active sessions',
  '🔑 Coduri Acces Temporar': '🔑 Temporary Access Codes',
  Dispeceri: 'Dispatchers',
  Administratori: 'Administrators',
  'Nume necunoscut': 'Unknown name',

  // Notification fallbacks used when a notification has no backend message.
  'Document expirare notificare': 'Document expiration notification',
  'Mesaj de sistem': 'System message',
  'Remorcă #': 'Trailer #',
  colete: 'packages',
  'Echipaj:': 'Crew:',
  'Preț:': 'Price:',
  'Document semnat #': 'Signed document #',
  'Șofer #': 'Driver #',

  // Tachograph and planning views
  'Ultimele 14 zile': 'Last 14 days',
  'Ultimele 30 zile': 'Last 30 days',
  'Repartizare pe zile': 'Breakdown by day',
  'Activitate soferi': 'Driver activity',
  'Vezi toate': 'View all',
  'Detalii zi': 'Day details',
  'Activitate pe zile': 'Daily activity',
  Azi: 'Today',

  // Driver and leave management
  'Ordine transporturi': 'Transport order',
  Concedii: 'Leave',
  Editare: 'Edit',
  'Zile (max.': 'Days (max.',
  'Calendar Concedii': 'Leave Calendar',
  'Ordine Transporturi': 'Transport Order',
  'Salariu profitabilitate': 'Salary used for profitability',
  'Gestionare Concedii': 'Leave Management',
  'Concediu Aprobat': 'Approved Leave',
  'Cereri de Concediu': 'Leave Requests',
  Aprobate: 'Approved',
  'Nu sunt cereri de concediu': 'There are no leave requests',
  'Note de aprobare': 'Approval notes',
  'Document Cerere': 'Request Document',
  'Descarca cererea': 'Download request',
  'Rapoarte Concedii': 'Leave Reports',
  'Vizualizare concedii pe anul curent': 'Current-year leave overview',

  // Support form and profile
  'Eroare la trimiterea mesajului. Vă rugăm să încercați din nou.': 'The message could not be sent. Please try again.',
  'Aplicația de email s-a deschis cu mesajul pregătit. Vă rugăm să trimiteți emailul.': 'Your email application opened with the message ready. Please send the email.',
  'Contactează suportul': 'Contact Support',
  'Introduceți numele dvs.': 'Enter your name',
  'Problema întâmpinată': 'Issue encountered',
  'Descrieți problema în detaliu...': 'Describe the issue in detail...',
  'Trimite mesaj': 'Send message',
  'Mesajul va fi trimis prin aplicația dvs. de email către general@atrux.ro': 'The message will be sent to general@atrux.ro using your email application',
  'Notă:': 'Note:',
  'Drag & Drop sau Click pentru a selecta': 'Drag and drop or click to select',
  'Eroare la actualizarea documentului': 'Error updating the document',
  'Ultima autentificare': 'Last sign-in',
  'Confirmare schimbare email': 'Confirm Email Change',
  'Eroare la trimitere': 'Sending error',
  'Parolele noi nu coincid.': 'The new passwords do not match.',
  'parolele coincid': 'passwords match',
  'Titlu Document': 'Document title',
  'Fus orar companie': 'Company time zone',
  'Descrierea problemei *': 'Problem description *',
  'Subiect *': 'Subject *',

  // AI analysis/chat interface. User prompts, answers, uploaded filenames and
  // extracted document content are deliberately kept outside this catalog.
  Caracteristici: 'Features',
  'Sfaturi pentru rezultate mai bune:': 'Tips for better results:',
  'Istoric Analize Recente': 'Recent Analysis History',
  Foto: 'Photo',
  'AI Chat Asistent': 'AI Chat Assistant',
  'Nivel de detaliu': 'Level of detail',
  Automat: 'Automatic',
  '• Pagini:': '• Pages:',
  '• Timp:': '• Time:',
  'Tobi · Asistent virtual': 'Tobi · Virtual Assistant',
  'SFATURI UTILE:': 'USEFUL TIPS:',
  'Scrie un mesaj...': 'Type a message...',
  TU: 'YOU',
  'Folosit:': 'Used:',
  'asistentul tau virtual Tobi': 'your virtual assistant Tobi',

  // Shared navigation and connection status chrome.
  'Arhiva CMR-uri': 'CMR Archive',
  'Canal personal:': 'Personal channel:',
  Conectat: 'Connected',
  Deconectat: 'Disconnected',
  Reconectare: 'Reconnect',
  'Reconectare...': 'Reconnecting...',

  // Short account and authentication labels that do not contain diacritics.
  // Keep them explicit: these strings previously bypassed the Romanian-copy
  // detector and therefore remained Romanian on otherwise English screens.
  Maxim: 'Maximum',
  Ascunde: 'Hide',
  'Minim 8 caractere': 'At least 8 characters',
  'minim 8 caractere': 'at least 8 characters',
  'Email trimis!': 'Email sent!',
  'Mesaj trimis!': 'Message sent!',
  'Adresa de email *': 'Email address *',
  'Nu ai primit codul?': "Didn't receive the code?",
  'Retrimite codul': 'Resend code',
  'Am trimis un cod de 6 cifre pe adresa ta de email': 'We sent a 6-digit code to your email address',
  'Email pentru codurile 2FA': 'Email for 2FA codes',
  ACTIVAT: 'ENABLED',
  'Introduci codul pentru a finaliza autentificarea': 'Enter the code to complete authentication',
  'Numele documentului': 'Document name',
  'Date transportator companie': 'Company carrier details',
  'Denumire transportator': 'Carrier name',
  'Email curent:': 'Current email:',
  'Email nou:': 'New email:',
  'Email trimis cu succes!': 'Email sent successfully!',

  // Notification centre actions.
  NOU: 'NEW',
  'Vezi Documentul': 'View Document',
  'Vezi Cererea': 'View Request',
  'Vezi Transportul': 'View Transport',

  // PTV optimisation UI. These remain in the compatibility pack until that
  // feature moves to semantic i18next keys; numerical option values are UI
  // choices rather than backend or user-authored business data.
  Livrare: 'Delivery',
  ACTIV: 'ACTIVE',
  'Dispatcher - Optimizare Rute': 'Dispatcher - Route Optimisation',
  puncte: 'points',
  'puncte de livrare': 'delivery points',
  Puncte: 'Points',
  'Puncte de Livrare': 'Delivery Points',
  'Punct de livrare': 'Delivery point',
  'Localitate livrare': 'Delivery locality',
  'Tipul punctului': 'Point type',
  'Ora Start': 'Start Time',
  'Tipuri de Puncte': 'Point Types',
  'DELIVERY - Livrare': 'DELIVERY - Delivery',
  'Cost Estimat': 'Estimated Cost',
  Anulat: 'Cancelled',
  'Status nedefinit': 'Undefined status',
  'Status Optimizare': 'Optimisation Status',
  'Timp estimat:': 'Estimated time:',
  'Date Returnate': 'Returned Data',
  'Taxe drum': 'Road tolls',
  'Costuri monetare': 'Monetary costs',
  'Cost pe km (RON)': 'Cost per km (RON)',
  'Cost pe kilometru (': 'Cost per kilometre (',
  'Ore Conducere Maxime/Zi (ore)': 'Maximum Driving Hours/Day (hours)',
  'Ore Lucru Maxime/Zi (ore)': 'Maximum Working Hours/Day (hours)',
  'Repaus Zilnic Minim (ore)': 'Minimum Daily Rest (hours)',
  '2 ore': '2 hours',
  '2.5 ore': '2.5 hours',
  '3 ore': '3 hours',
  '3.5 ore': '3.5 hours',
  '4 ore': '4 hours',
  '4.5 ore': '4.5 hours',
  '4.5 ore (Standard EU)': '4.5 hours (EU standard)',
  '5 ore': '5 hours',
  '6 ore': '6 hours',
  '7 ore': '7 hours',
  '8 ore': '8 hours',
  '9 ore': '9 hours',
  '9 ore (Standard EU)': '9 hours (EU standard)',
  '9 ore (Redus EU)': '9 hours (EU reduced)',
  '10 ore': '10 hours',
  '10 ore (Extins EU)': '10 hours (EU extended)',
  '11 ore': '11 hours',
  '11 ore (Standard EU)': '11 hours (EU standard)',
  '12 ore': '12 hours',
  '13 ore': '13 hours',
  '14 ore': '14 hours',
  'Greutate & Axe': 'Weight & Axles',
  Niciuna: 'None',
  Program: 'Schedule',
  'Program de Lucru': 'Working Hours',
  'Rezultatele rutei optimizate': 'Optimised route results',
  'Optimizat cu PTV Route Optimization API': 'Optimised with the PTV Route Optimization API',
  'Progres:': 'Progress:',
  secunde: 'seconds',

  // Shared option catalogs consumed by legacy aviz and driver forms.
  Kilograme: 'Kilograms',
  Grame: 'Grams',
  Mililitri: 'Millilitres',
  Tone: 'Tonnes',
  Palet: 'Pallet',
  'Contract de munca': 'Employment contract',

  // Transport and trip-sheet fallbacks
  neînregistrat: 'unregistered',
  'se încarcă...': 'loading...',
  'Detalii camion indisponibile': 'Truck details unavailable',
  'Ultima foaie': 'Latest trip sheet',
  'Foaie de parcurs': 'Trip sheet',

  // Fleet and profitability
  Expirate: 'Expired',
  'Luna raportului': 'Report month',
  'Salariu grup': 'Group salary',
  'Fix camion': 'Truck fixed cost',
  'Toate camioanele': 'All trucks',
  'Note grup respins': 'Rejected-group notes',
  'Perioada documentului': 'Document period',
  'Moneda documentului': 'Document currency',
  'Camion necitit': 'Truck not recognised',
  'Furnizor necitit': 'Supplier not recognised',
  'Salariu alocat grupului': 'Salary allocated to the group',
  'egal pe transporturi': 'equally across transports',
  'Alege camion': 'Choose a truck',

  // Transport status workflow
  'Eroare la incarcarea transportului': 'Error loading the transport',
  'Inapoi la Transporturi': 'Back to Transports',
  'Status Remorca': 'Trailer Status',
  'Camion Incarcat': 'Truck Loaded',
  'Probleme Camion': 'Truck Issues',
  'Descriere Remorca': 'Trailer Description',
  'Alege fotografii': 'Choose photos',
  'Trimite fotografii': 'Submit photos',
  'Alege fotografie': 'Choose a photo',

  // Quotation option labels
  'Camion cu semiremorcă (EUR_TRAILER_TRUCK)': 'Semi-trailer truck (EUR_TRAILER_TRUCK)',
});

// Template literals cannot be represented by exact lookup entries. Every rule
// is anchored to an application-owned Romanian shell; captured values are
// preserved unless they are one of the finite labels translated above.
export const legacyEnglishRemainingPatterns = Object.freeze([
  [
    /^Oprirea (\d+)$/u,
    'Stop $1',
  ],
  [
    /^Retrimite codul \((\d+)s\)$/,
    'Resend code ($1s)',
  ],
  [
    /^(\d+) (?:zi|zile)$/,
    (_, count) => `${count} ${Number(count) === 1 ? 'day' : 'days'}`,
  ],
  [
    /^(.+) · verificat pentru (.+)$/,
    (_, label, checkedFor) => `${translateAvailabilityLabel(label)} · checked for ${checkedFor === 'azi' ? 'today' : checkedFor}`,
  ],
  [
    /^(\d+) (?:camion a fost programat|camioane au fost programate|camionul) pentru sincronizare\.$/,
    (_, count) => `${count} ${Number(count) === 1 ? 'truck was' : 'trucks were'} scheduled for synchronisation.`,
  ],
  [
    /^(\d+) vehicule cu acte$/,
    (_, count) => `${count} ${Number(count) === 1 ? 'vehicle' : 'vehicles'} with documents`,
  ],
  [
    /^(?:(\d+) )?(autoturism|autoturisme|dubă|dube) în flotă$/,
    (_, count, vehicle) => {
      const nouns = {
        autoturism: 'passenger car',
        autoturisme: 'passenger cars',
        dubă: 'van',
        dube: 'vans',
      };
      return `${count ? `${count} ` : ''}${nouns[vehicle]} in fleet`;
    },
  ],
  [
    /^(.+) din (.+) zile$/,
    '$1 of $2 days',
  ],
]);
